import React, {createContext, useContext, useState} from 'react';
import {Typography} from 'antd';
import {useParams} from 'react-router-dom';
import {TenetEditor} from '@acuwize/openapi-editor';
import {useAuth} from '../context/auth-context';
import CommentsPanel from '../components/comments-panel';

// Mounted at /:env/standards/:wg/:key/:version?/:view? — editor's internal
// router expects this exact shape, so by the time <TenetEditor> renders the
// URL is something it can match. (Mounting it at '/' makes useRouteMatch
// return null inside library.js, which crashes the whole tree — see
// portal/TODO-FROM-EDITOR.md bridge #4.)
//
// Right-pane composition follows the editor's documented dev contract
// (editor/tenetai-editor-README.md §11.1):
//   ?ai=on / ?ai=off    — AI tab on/off; portal passes aiEnabled=undefined
//                         so editor's isAIEnabled() chain (URL → window
//                         → env → false) is the authority.
//   ?devSlots=<csv>     — replaces portal's default slot list with the
//                         named slots. Known names ("comments") use the
//                         real portal component; unknown names render
//                         a DevSlotPanel placeholder so editor team can
//                         exercise the §10(e) tab UX without portal
//                         shipping a real component first.
//   (none)              — defaults to the single Comments slot.
//
// AnchorContext bridges editor's per-property anchor callback into the
// CommentsPanel inside the right-pane slot — slot mechanism takes a
// static JSX element so the panel can't receive prop updates directly;
// it subscribes to context instead.

const AnchorContext = createContext({spec_path: null, json_pointer: null});

function CommentsPanelWithAnchor() {
    const {spec_path, json_pointer} = useContext(AnchorContext);
    if (!spec_path) {
        return (
            <div className="portal-dashboard__no-anchor">
                Click a property in the schema view to start a thread.
            </div>
        );
    }
    return <CommentsPanel spec_path={spec_path} json_pointer={json_pointer} />;
}

// Placeholder for unknown slot names declared via ?devSlots=. Mirrors the
// shape editor's standalone main.jsx renders so the visual contract is
// identical when portal swaps in a real component later.
function DevSlotPanel({name}) {
    const Title = name.charAt(0).toUpperCase() + name.slice(1);
    return (
        <div style={{padding: 16, height: '100%', overflow: 'auto'}}>
            <Typography.Title level={4} style={{marginTop: 0}}>{Title}</Typography.Title>
            <Typography.Paragraph type="secondary">
                Portal placeholder for the <Typography.Text code>{name}</Typography.Text> slot.
                When portal ships a real component for this name, the placeholder is replaced
                automatically (no editor change needed).
            </Typography.Paragraph>
        </div>
    );
}

// Map known slot names to real portal components; anything else renders
// a DevSlotPanel placeholder. Add to this map as portal ships more
// right-pane content.
function componentForSlot(name) {
    if (name.toLowerCase() === 'comments') return <CommentsPanelWithAnchor />;
    return <DevSlotPanel name={name} />;
}

function buildRightPanelSlots() {
    const defaults = [{
        key: 'comments',
        label: 'Comments',
        component: <CommentsPanelWithAnchor />,
    }];

    if (typeof window === 'undefined') return defaults;
    const params = new URLSearchParams(window.location.search);
    const csv = params.get('devSlots');
    if (!csv) return defaults;

    const names = csv.split(',').map((s) => s.trim()).filter(Boolean);
    if (names.length === 0) return defaults;

    return names.map((name) => ({
        key: name,
        label: name.charAt(0).toUpperCase() + name.slice(1),
        component: componentForSlot(name),
    }));
}

function EditorPage() {
    const {user} = useAuth();
    const {wg, key} = useParams();
    const [anchor, setAnchor] = useState({spec_path: null, json_pointer: null});

    // Initial anchor reflects the route — when a user deep-links to a spec
    // before clicking any property, we still want CommentsPanel to know
    // which spec it's anchored to. The json_pointer fills in once the user
    // clicks something.
    const initialSpecPath = `${wg}/${key}`;
    if (anchor.spec_path === null && wg && key) {
        // Lazy init via setState only on first render (avoids a useEffect).
        setAnchor({spec_path: initialSpecPath, json_pointer: null});
    }

    return (
        <AnchorContext.Provider value={anchor}>
            <div className="portal-dashboard portal-dashboard--editor">
                <TenetEditor
                    user={user}
                    workgroup={wg}
                    spec_path={initialSpecPath}
                    rightPanelSlots={buildRightPanelSlots()}
                    onCommentAnchorChange={(a) => {
                        setAnchor({
                            spec_path: a.spec_path || initialSpecPath,
                            json_pointer: a.json_pointer,
                        });
                    }}
                    onSpecChange={() => {
                        // Day 12: re-fetch comments / log audit. v0 = no-op.
                    }}
                />
            </div>
        </AnchorContext.Provider>
    );
}

export default EditorPage;
